import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Briefcase, MapPin } from 'lucide-react';

interface ExperienceItem {
  position: string;
  company: string;
  logo: string;
  location: string;
  period: string;
  description: string;
}

const experienceData = {
  en: {
    title: 'Experience',
    subtitle: 'My professional journey and career milestones.',
    experience: [
      {
        position: 'Full Stack Engineer',
        company: 'Ultatel',
        logo: '/images/ultatel_logo.jpg',
        location: 'Alexandria, Egypt',
        period: 'Mar 2024 - Present',
        description:
          'Built the Direct Meetings module across Web, Electron desktop, and Android (Angular, Capacitor), integrating Jitsi Meet video conferencing, detached pop-out windows, and reactive session state. Engineered the Direct Meetings backend in C#/.NET 8 with SignalR and Redis, managing meeting state machines, distributed participant presence, and external guest admission flows. Contributed to NestJS identity microservices and C# APIs to issue tenant-scoped conference JWTs, enforcing strict multi-tenant data boundaries, and built a click-to-call Chrome extension. Awarded the company-wide Top Performer Award (2026) by the CEO.',
      },
      {
        position: 'Full Stack Engineer',
        company: 'Modeso',
        logo: '/images/modeso_logo.jpg',
        location: 'Alexandria, Egypt',
        period: 'Aug 2023 - Feb 2024',
        description:
          "Delivered core merchant and user features for TWINT Super Deals (embedded marketplace within Switzerland's payment app, serving 4M+ users), building modular Angular/NgRx components under Hexagonal Architecture. Developed RESTful backend endpoints and MongoDB queries in Node.js and Express across microservices, integrating RabbitMQ message queues for asynchronous event processing.",
      },
      {
        position: 'Desktop Application Developer (Contract)',
        company: 'SysBlend Solutions',
        logo: '/images/sysblend_logo.jpg',
        location: 'Remote',
        period: 'Nov 2021 - Dec 2022',
        description:
          'Engineered enterprise JavaFX ERP client for Zayir Alkhayr Charitable Foundation spanning 130+ views and dialogs across 8 operational modules (inventory, clinics, payroll), using worker threads to offload heavy API payloads.',
      },
      {
        position: 'Software Engineer (Founding Team)',
        company: 'Code Clinic',
        logo: '/images/code_clinic_logo.png',
        location: 'Alexandria, Egypt',
        period: 'May 2019 - Aug 2023',
        description:
          'Co-founded a software business, delivering enterprise platforms to 120+ clients across 6 countries. Engineered offline-first JavaFX restaurant POS with a Node.js/PostgreSQL cloud sync hub and companion waiter mobile app, enabling real-time order tracking and kitchen ticket routing. Built specialized optical ERP and clinic examination software, managing patient eye-refraction records, custom optical workshop orders, and deposit-based billing lifecycles.',
      },
    ],
  },
  ar: {
    title: 'الخبرة المهنية',
    subtitle: 'مسيرتي المهنية وأبرز المحطات والإنجازات في تطوير الأنظمة.',
    experience: [
      {
        position: 'مهندس Full Stack',
        company: 'Ultatel',
        logo: '/images/ultatel_logo.jpg',
        location: 'الإسكندرية، مصر',
        period: 'مارس 2024 - الحالي',
        description:
          'بناء منظومة Direct Meetings عبر الويب والديسكتوب (Electron) والأندرويد (Angular و Capacitor) مع دمج مؤتمرات الفيديو Jitsi Meet والنوافذ العائمة وإدارة الحالة التفاعلية. هندسة الخادم الخلفي لاجتماعات Direct Meetings بـ C#/.NET 8 مع SignalR و Redis لإدارة حالات الاجتماع، وتتبع حضور المشاركين الموزع، وتدفقات انضمام الضيوف. المساهمة في الخدمات المصغرة للهوية بـ NestJS وواجهات C# لإصدار رموز JWT الخاصة بالمؤتمرات مع عزل بيانات المستأجرين، وبناء إضافة كروم للاتصال السريع. نيل جائزة Top Performer لعام 2026 على مستوى الشركة من الرئيس التنفيذي تقديراً للإسهامات التقنية.',
      },
      {
        position: 'مهندس Full Stack',
        company: 'Modeso',
        logo: '/images/modeso_logo.jpg',
        location: 'الإسكندرية، مصر',
        period: 'أغسطس 2023 - فبراير 2024',
        description:
          'تطوير الميزات الجوهرية للتجار والمستخدمين لمنصة TWINT Super Deals (منصة تجارة مدمجة داخل تطبيق المدفوعات السويسري تخدم أكثر من 4 ملايين مستخدم)، وبناء مكونات Angular/NgRx وفق المعمارية السداسية Hexagonal Architecture. تطوير واجهات خلفية RESTful واستعلامات MongoDB باستخدام Node.js و Express عبر الخدمات المصغرة، ودمج RabbitMQ لمعالجة الأحداث غير المتزامنة.',
      },
      {
        position: 'مطور تطبيقات سطح المكتب (تعاقد)',
        company: 'SysBlend Solutions',
        logo: '/images/sysblend_logo.jpg',
        location: 'عن بُعد',
        period: 'نوفمبر 2021 - ديسمبر 2022',
        description:
          'هندسة واجهة سطح مكتب ERP لمؤسسة زائر الخير الخيرية باستخدام JavaFX تغطي أكثر من 130 واجهة ومربع حوار عبر 8 موديولات تشغيلية (المخازن، العيادات، الرواتب)، مع استخدام خيوط معالجة غير متزامنة لضمان سلاسة الواجهة أثناء معالجة البيانات.',
      },
      {
        position: 'مهندس برمجيات (فريق التأسيس)',
        company: 'Code Clinic',
        logo: '/images/code_clinic_logo.png',
        location: 'الإسكندرية، مصر',
        period: 'مايو 2019 - أغسطس 2023',
        description:
          'المشاركة في تأسيس شركة برمجيات وتقديم منصات للمؤسسات لأكثر من 120 عميلاً في 6 دول. هندسة نظام نقاط بيع للمطاعم يعمل دون إنترنت (Offline-first) باستخدام JavaFX مع خادم مزامنة سحابي بـ Node.js/PostgreSQL وتطبيق لوحي للويتر لتتبع الطلبات وإرسالها للمطبخ بالزمن الحقيقي. بناء برمجيات متخصصة للعيادات ومحلات النظارات لإدارة سجلات فحص العيون، وأوامر الورش الفنية، ودورات الفواتير والمدفوعات.',
      },
    ],
  },
};

export function Experience() {
  const { language, isRTL } = useLanguage();
  const data = experienceData[language];

  const TimelineItem = ({ item }: { item: ExperienceItem }) => (
    <div className={`flex gap-6`}>
      <div className="flex flex-col items-center">
        <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
          <Briefcase className="w-6 h-6 text-white" />
        </div>
        <div className="w-px bg-gradient-to-b from-green-600 to-emerald-600 flex-1 mt-4"></div>
      </div>
      <div className="flex-1 pb-12">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
          <div className={`mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-lg font-bold text-white mb-1">{item.position}</h3>
            <div className={`flex items-center gap-3 mb-2`}>
              <img
                src={item.logo}
                alt={`${item.company} logo`}
                className="w-8 h-8 rounded-lg object-cover"
              />
              <p className="text-green-300 font-medium">{item.company}</p>
            </div>
            <div className={`flex items-center gap-4 mt-2 text-sm text-slate-300`}>
              <div className={`flex items-center gap-1`}>
                <MapPin className="w-4 h-4" />
                <span>{item.location}</span>
              </div>
              <span className="text-slate-400">•</span>
              <span>{item.period}</span>
            </div>
          </div>
          <p className={`text-slate-300 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
            {item.description}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <section id="experience" className="py-20 px-4 sm:px-6 lg:px-8 relative -mt-20">
      <div className="max-w-4xl mx-auto">
        <div className={`text-center mb-16 ${isRTL ? 'text-right' : 'text-left'}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{data.title}</h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">{data.subtitle}</p>
        </div>

        <div className="space-y-8">
          {data.experience.map((item, index) => (
            <TimelineItem key={index} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}
